import {
  actionStoreSelector,
  useActionStore,
} from '@repo/editor/src/stores/actionStore/actionStore';
import RemoteDataStatus from '@repo/editor/src/types/RemoteDataStatus';
import isAfter from 'date-fns/isAfter';
import { create } from 'zustand';

import { announcement } from './config';
import { State } from './types';

export const useAnnouncementStore = create<State>((set, get) => ({
  /**
   * modal state
   * controls whether the modal is open
   */
  isOpen: false,

  /**
   * stores announcement items
   */
  items: null,

  /**
   * badge to show on top of the announcement modal
   */
  badge: announcement.badge,

  /**
   * initialize the store based on the user
   */
  init: async (user, growthBook): Promise<void> => {
    const now = new Date();
    const announcementIsValid =
      isAfter(now, announcement.starts) &&
      isAfter(announcement.ends, now) &&
      (announcement.feature ? growthBook?.isOn(announcement.feature) : true);

    if (!user || !announcementIsValid) {
      set({ isOpen: false });
      return;
    }

    set({
      items: announcement.items.map((x) => ({
        ...x,
        offer: x.offer?.showIf(user) ? x.offer : undefined,
      })),
    });

    await useActionStore.getState().init();

    if (useActionStore.getState().status !== RemoteDataStatus.Done) {
      return;
    }

    const announcementRead = actionStoreSelector
      .actionIds(useActionStore.getState())
      ?.includes(announcement.id);

    const userCreatedAfterAnnouncementStarts = isAfter(
      new Date(user.createdAt),
      announcement.starts
    );

    set({
      isOpen:
        !announcementRead &&
        (announcement.visibleFor === 'allUsers' ||
          !userCreatedAfterAnnouncementStarts),
    });
  },

  closeModal: (): void => {
    if (!get().isOpen) {
      return;
    }

    set({ isOpen: false });
  },

  markAsRead: async (): Promise<void> => {
    if (
      useActionStore
        .getState()
        .actions?.find((action) => action.id === announcement.id)
    ) {
      return;
    }

    await useActionStore.getState().createAction(announcement.id);
  },
}));

export const announcementStoreSelector = {
  isOpen: (state: State): State['isOpen'] => state.isOpen,
  items: (state: State): State['items'] => state.items,
  badge: (state: State): State['badge'] => state.badge,
  closeModal: (state: State): State['closeModal'] => state.closeModal,
  markAsRead: (state: State): State['markAsRead'] => state.markAsRead,
};
